import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'

import { getLaborMetrics } from '@/api/clarity'
import { formatMetric } from '@/hooks/clarity/util'
import { LaborMetricsModel, MetricCardComparisonModel } from '@/types/clarity'
import { dateToString } from '@/utils'
import { DateType, getDateRange, getTrendingDate } from '@/utils/date'

const COMPANY_SYSTEM = 'Branch Total'

export default function useLaborMetrics(
  startDate: string,
  endDate: string,
  dateFilterType: DateType,
  branches: string
) {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<Error | null>(null)
  const [branchData, setBranchData] = useState<LaborMetricsModel[]>([])
  const [branchTrendData, setBranchTrendData] = useState<LaborMetricsModel[]>(
    []
  )
  const [companyData, setCompanyData] = useState<LaborMetricsModel>()
  const [companyTrendData, setCompanyTrendData] = useState<LaborMetricsModel>()
  const [metricCardData, setMetricCardData] =
    useState<MetricCardComparisonModel<LaborMetricsModel>>()

  const fetchData = useMemo(() => {
    return async () => {
      try {
        const { branchMetrics, companyMetrics } = await fetchLaborMetrics(
          branches,
          startDate,
          endDate
        )
        setLocalTrendingData(branches, setMetricCardData)

        setBranchData(branchMetrics)
        setCompanyData(companyMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate])

  const fetchTrendData = useMemo(() => {
    return async () => {
      try {
        const trendingDate = getTrendingDate(
          { from: new Date(startDate), to: new Date(endDate) },
          dateFilterType
        ) as DateRange

        const { branchMetrics, companyMetrics } = await fetchLaborMetrics(
          branches,
          dateToString(trendingDate, 'from'),
          dateToString(trendingDate, 'to')
        )

        setBranchTrendData(branchMetrics)
        setCompanyTrendData(companyMetrics)
      } catch (err) {
        setError(
          new Error(
            err instanceof Error ? err.message : 'An unknown error occurred'
          )
        )
      }
    }
  }, [branches.toString(), startDate, endDate, dateFilterType])

  const fetchAllData = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      await Promise.all([fetchData(), fetchTrendData()])
    } catch (err) {
      setError(
        new Error(
          err instanceof Error ? err.message : 'An unknown error occurred'
        )
      )
    } finally {
      setLoading(false)
    }
  }, [fetchData, fetchTrendData])

  const refetch = useCallback(() => {
    console.log('Refetching labor data...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    branchData,
    companyData,
    branchTrendData,
    companyTrendData,
    metricCardData,
    loading,
    error,
    refetch,
  }
}

async function setLocalTrendingData(
  branches: string,
  setMetricCardData: (
    data: MetricCardComparisonModel<LaborMetricsModel>
  ) => void
) {
  const mtdDate = getDateRange('month-to-date')
  const ytdDate = getDateRange('year-to-date')
  const mtdTrendingDate = mtdDate.trending as DateRange
  const ytdTrendingDate = ytdDate.trending as DateRange

  const { companyMetrics: mCompanyMetrics } = await fetchLaborMetrics(
    branches,
    dateToString(mtdDate, 'from'),
    dateToString(mtdDate, 'to')
  )

  const { companyMetrics: mCompanyMetricsTrending } = await fetchLaborMetrics(
    branches,
    dateToString(mtdTrendingDate, 'from'),
    dateToString(mtdTrendingDate, 'to')
  )

  const { companyMetrics: yCompanyMetrics } = await fetchLaborMetrics(
    branches,
    dateToString(ytdDate, 'from'),
    dateToString(ytdDate, 'to')
  )

  const { companyMetrics: yCompanyMetricsTrending } = await fetchLaborMetrics(
    branches,
    dateToString(ytdTrendingDate, 'from'),
    dateToString(ytdTrendingDate, 'to')
  )

  setMetricCardData({
    currMetricsMonthly: mCompanyMetrics,
    currMetricsYearly: yCompanyMetrics,
    prevMetricsMonthly: mCompanyMetricsTrending,
    prevMetricsYearly: yCompanyMetricsTrending,
  })
}

async function fetchLaborMetrics(
  branches: string,
  startDate: string,
  endDate: string
): Promise<{
  branchMetrics: LaborMetricsModel[]
  companyMetrics: LaborMetricsModel
}> {
  try {
    const response = await getLaborMetrics(branches, startDate, endDate)

    const metrics: LaborMetricsModel[] = response.map(
      (responseMetrics: any) => {
        return setMetric(
          responseMetrics.branch_name,
          responseMetrics.total_labor_percentage,
          responseMetrics.manager_percentage,
          responseMetrics.barista_percentage,
          responseMetrics.training_percentage,
          responseMetrics.marketing_percentage,
          responseMetrics.meeting_percentage,
          responseMetrics.total_hours,
          responseMetrics.tip_eligible_manager_hours,
          responseMetrics.non_tip_manager_hours,
          responseMetrics.tip_eligible_barista_hours,
          responseMetrics.non_tip_barista_hours,
          responseMetrics.training_hours,
          responseMetrics.marketing_hours,
          responseMetrics.meeting_hours,
          responseMetrics.non_regular_labor_cost_percentage,
          responseMetrics.total_barista_cost,
          responseMetrics.total_labor_cost,
          responseMetrics.total_manager_cost,
          responseMetrics.total_marketing_cost,
          responseMetrics.total_training_cost,
          responseMetrics.unit_labor_cost,
          responseMetrics.tip_percentage,
          responseMetrics.overtime_percentage,
          responseMetrics.charts_data
        )
      }
    )

    const branchMetrics = metrics.filter(
      (metric) => metric.stand !== COMPANY_SYSTEM
    )
    const companyMetrics = metrics.filter(
      (metric) => metric.stand === COMPANY_SYSTEM
    )

    return {
      branchMetrics,
      companyMetrics: companyMetrics[0],
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function setMetric(
  stand: string,
  totalLaborPercentage: number,
  managerPercentage: number,
  baristaPercentage: number,
  trainingPercentage: number,
  marketingPercentage: number,
  meetingPercentage: number,
  totalHours: number,
  tipEligibleManagerHours: number,
  nonTipManagerHours: number,
  tipEligibleBaristaHours: number,
  nonTipBaristaHours: number,
  trainingHours: number,
  marketingHours: number,
  meetingHours: number,
  nonRegularLaborCostPercentage: number,
  totalBaristaCost: number,
  totalLaborCost: number,
  totalManagerCost: number,
  totalMarketingCost: number,
  totalTrainingCost: number,
  unitLaborCost: number,
  tipsPercentage: number,
  overtimePercentage: number,
  chartsData: {
    tips_by_shifts: number[][]
    tip_bar_chart: number[][]
  }
): LaborMetricsModel {
  return {
    stand: stand === 'system_total' ? 'Branch Total' : stand,
    laborRatio: formatMetric(totalLaborPercentage, 1),
    laborHours: formatMetric(totalHours, 0),
    managerLaborRatio: formatMetric(managerPercentage, 1),
    managerTipHours: formatMetric(tipEligibleManagerHours, 0),
    managerNoTipHours: formatMetric(nonTipManagerHours, 0),
    baristaLaborRatio: formatMetric(baristaPercentage, 1),
    baristaTipHours: formatMetric(tipEligibleBaristaHours, 0),
    baristaNoTipHours: formatMetric(nonTipBaristaHours, 0),
    trainingLaborRatio: formatMetric(trainingPercentage, 1),
    trainingLaborHours: formatMetric(trainingHours, 0),
    marketingLaborRatio: formatMetric(marketingPercentage, 1),
    marketingLaborHours: formatMetric(marketingHours, 0),
    meetingLaborRatio: formatMetric(meetingPercentage, 1),
    meetingLaborHours: formatMetric(meetingHours, 0),
    nonRegularLaborCostPercentage: formatMetric(
      nonRegularLaborCostPercentage,
      1
    ),
    totalBaristaCost: formatMetric(totalBaristaCost, 0),
    totalLaborCost: formatMetric(totalLaborCost, 0),
    totalManagerCost: formatMetric(totalManagerCost, 0),
    totalMarketingCost: formatMetric(totalMarketingCost, 0),
    totalTrainingCost: formatMetric(totalTrainingCost, 0),
    unitLaborCost: formatMetric(unitLaborCost, 2),
    tipPercentage: formatMetric(tipsPercentage, 1),
    overtimePercentage: formatMetric(overtimePercentage, 2),
    chartsData: {
      tipsByShiftsRaw: chartsData?.tips_by_shifts || [],
      tipBarChartRaw: chartsData?.tip_bar_chart || [],
      hoursByTypeRaw: {
        manager: formatMetric(tipEligibleManagerHours + nonTipManagerHours, 0),
        barista: formatMetric(tipEligibleBaristaHours + nonTipBaristaHours, 0),
        training: formatMetric(trainingHours, 0),
        marketing: formatMetric(marketingHours, 0),
      },
    },
  }
}
